import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, tap } from 'rxjs';
import { Tc } from '../../core/_infrastructure/types/common';
import { PeriodoTipoSolicitudViewModel, PeriodoDatosCaCamViewModel, CASelectedViewModel } from '../buscadores/solicitudes/buscador-solicitud-cam.types';
import { TCampaniaEspecificaViewModel } from '../../_mantenimiento/campanias/campaniasespecificas/campaniaespecifica.types';
import { PeriodoCamItemViewModel } from '../../_mantenimiento/campanias/periodos/datosbasicos/periodocam.types';
import { ListaInspeccionPeriodoViewModel } from '../../_mantenimiento/campanias/periodos/inspecciones/inspecciones.types';

@Injectable()
export class CampaniasGlobalService {
    private CAMP_GLOBAL_BASE_URL = this.baseUrl + 'api/campanias/global/';
    private CAMP_MTO_BASE_URL = this.baseUrl + 'api/mantenimiento/campaniaespecifica/';
    private INSPECCIONES_DE_PERIODO_URL = this.baseUrl + 'api/mantenimiento/periodos/periodo/';

    private estadosUnidadInspeccion: Tc[] = [];
    private resultadosInspeccionCam: Tc[] = [];
    private estadosAceptacion: Tc[] = [];
    private estadosValidacion: Tc[] = [];
    private estadosSolicitudCam: Tc[] = [];

    constructor(
        private http: HttpClient,

        @Inject('BASE_URL') private baseUrl: string,
    ) {}

    getCampaniasByProfile(activa: number): Observable<Tc[]> {
        return this.http.get<Tc[]>(this.CAMP_GLOBAL_BASE_URL + 'CampaniasByProfile/' + activa);
    }

    getCampaniasEnPeriodo(habilitados: number): Observable<Tc[]> {
        return this.http.get<Tc[]>(this.CAMP_GLOBAL_BASE_URL + 'CampaniasEnPeriodo/' + habilitados);
    }

    getPeriodosByCampania(idCampania: number, activos: number): Observable<Tc[]> {
        return this.http.get<Tc[]>(this.CAMP_GLOBAL_BASE_URL + 'PeriodosByCampania/' + idCampania + '/' + activos);
    }

    /**
     * Este método obtiene la lista de periodos (objeto completo).
     * Además, incluirá aquellos periodos habilitados o no en función del parámetro habilitados
     * @param idCampania
     * @param habilitados
     * @returns Observable<PeriodoCamItemViewModel[]>
     */
    getPeriodosByCampaniaCompleta(idCampania: number, habilitados: number): Observable<PeriodoCamItemViewModel[]> {
        return this.http.get<PeriodoCamItemViewModel[]>(this.CAMP_GLOBAL_BASE_URL + 'PeriodosByCampaniaCompleta/' + idCampania + '/' + habilitados);
    }

    /**
     * Este método es similar a getPeriodosByCampaniaCompleta pero devolviendo solo recintos PI habilitados.
     * @param idCampania
     * @returns Observable<PeriodoCamItemViewModel[]>
     */
    getPeriodosPIHabilitados(idCampania: number): Observable<PeriodoCamItemViewModel[]> {
        return this.http.get<PeriodoCamItemViewModel[]>(this.CAMP_GLOBAL_BASE_URL + 'PeriodosPIHabilitados/' + idCampania);
    }

    getTiposSolicitudCam(idPeriodo: number): Observable<PeriodoTipoSolicitudViewModel[]> {
        return this.http.get<PeriodoTipoSolicitudViewModel[]>(this.CAMP_GLOBAL_BASE_URL + 'TiposSolicitudByPeriodo/' + idPeriodo);
    }

    getCAsByPeriodo(idPeriodo?: number): Observable<PeriodoDatosCaCamViewModel[]> {
        return this.http.get<PeriodoDatosCaCamViewModel[]>(this.CAMP_GLOBAL_BASE_URL + 'GetCAByPeriodo/' + idPeriodo);
    }

    getEstadosSolicitudCam(): Observable<Tc[]> {
        if (this.estadosSolicitudCam?.length > 0) {
            return of(this.estadosSolicitudCam);
        }
        return this.http.get<Tc[]>(this.CAMP_GLOBAL_BASE_URL + 'GetEstadosSolicitudCam/').pipe(
            tap(res => {
                this.estadosSolicitudCam = res;
            }),
        );
    }

    getEstadosValidacionSolicitudCam(): Observable<Tc[]> {
        if (this.estadosValidacion?.length > 0) {
            return of(this.estadosValidacion);
        }
        return this.http.get<Tc[]>(this.CAMP_GLOBAL_BASE_URL + 'GetEstadosValidacion/').pipe(
            tap(res => {
                this.estadosValidacion = res;
            }),
        );
    }

    getEstadosAceptacionSolicitudCam(): Observable<Tc[]> {
        if (this.estadosAceptacion?.length > 0) {
            return of(this.estadosAceptacion);
        }
        return this.http.get<Tc[]>(this.CAMP_GLOBAL_BASE_URL + 'GetEstadosAceptacion/').pipe(
            tap(res => {
                this.estadosAceptacion = res;
            }),
        );
    }

    getEAsByCA(param$: CASelectedViewModel): Observable<Tc[]> {
        let suffix = '';
        if (param$.idPeriodo) {
            suffix += param$.idPeriodo;
            if (param$.idCA) {
                suffix += '/' + param$.idCA;
            }
        }
        return this.http.get<Tc[]>(this.CAMP_GLOBAL_BASE_URL + 'GetEAsByCA/' + suffix);
    }

    getCampaniaById(id: number): Observable<TCampaniaEspecificaViewModel> {
        return this.http.get<TCampaniaEspecificaViewModel>(this.CAMP_MTO_BASE_URL + id);
    }

    getEstadosUnidadInspeccion(): Observable<Tc[]> {
        if (this.estadosUnidadInspeccion?.length > 0) {
            return of(this.estadosUnidadInspeccion);
        }
        return this.http.get<Tc[]>(this.CAMP_GLOBAL_BASE_URL + 'GetEstadosUi/').pipe(
            tap(res => {
                this.estadosUnidadInspeccion = res;
            }),
        );
    }

    /**
     * Este método obtiene la lista de tipos de inspección para el periodo indicado por parámetro.
     */
    getListaInspeccionesDePeriodo(id: number): Observable<ListaInspeccionPeriodoViewModel> {
        return this.http.get<ListaInspeccionPeriodoViewModel>(this.INSPECCIONES_DE_PERIODO_URL + 'ListaInspecciones/' + id);
    }

    getListaResultadosInspeccionCam(): Observable<Tc[]> {
        if (this.resultadosInspeccionCam?.length > 0) {
            return of(this.resultadosInspeccionCam);
        }
        return this.http.get<Tc[]>(this.CAMP_GLOBAL_BASE_URL + 'ResultadosInspeccionCam').pipe(
            tap(res => {
                this.resultadosInspeccionCam = res;
            }),
        );
    }
}
